import { supabase, isConfigured } from './supabase';

export const PORTAL_ASSET_BUCKET = 'portal-assets';

export function normalizePortalAssetPart(value = '', fallback = '') {
  const text = String(value || fallback || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '');
  return text || fallback;
}

export function normalizePortalAsset(row = {}) {
  if (!row || typeof row !== 'object') return row;
  return {
    ...row,
    entityRef: row.entity_ref ?? row.entityRef ?? 'global',
    assetKey: row.asset_key ?? row.assetKey,
    assetVersion: row.asset_version ?? row.assetVersion,
    storageBucket: row.storage_bucket ?? row.storageBucket ?? PORTAL_ASSET_BUCKET,
    storagePath: row.storage_path ?? row.storagePath,
    publicUrl: row.public_url ?? row.publicUrl,
    contentHash: row.content_hash ?? row.contentHash,
    contentType: row.content_type ?? row.contentType,
    byteSize: row.byte_size ?? row.byteSize,
    originalName: row.original_name ?? row.originalName,
    cacheControl: row.cache_control ?? row.cacheControl,
    isDemo: row.is_demo ?? row.isDemo ?? false,
    publishedBy: row.published_by ?? row.publishedBy,
    publishedAt: row.published_at ?? row.publishedAt,
    supersededAt: row.superseded_at ?? row.supersededAt,
  };
}

export function currentPortalAsset(assets = [], { scope = 'portal', entityRef = 'global', assetKey = '' } = {}) {
  const wantedScope = normalizePortalAssetPart(scope, 'portal');
  const wantedEntity = normalizePortalAssetPart(entityRef, 'global');
  const wantedKey = normalizePortalAssetPart(assetKey, 'asset');

  return (assets || [])
    .map(normalizePortalAsset)
    .filter(asset => (
      normalizePortalAssetPart(asset?.scope, 'portal') === wantedScope
      && normalizePortalAssetPart(asset?.entity_ref ?? asset?.entityRef, 'global') === wantedEntity
      && normalizePortalAssetPart(asset?.asset_key ?? asset?.assetKey, 'asset') === wantedKey
      && String(asset?.status || 'published').toLowerCase() === 'published'
    ))
    .sort((a, b) => {
      const versionDiff = Number(b.asset_version ?? b.assetVersion ?? 0) - Number(a.asset_version ?? a.assetVersion ?? 0);
      if (versionDiff) return versionDiff;
      return String(b.published_at ?? b.publishedAt ?? '').localeCompare(String(a.published_at ?? a.publishedAt ?? ''));
    })[0] || null;
}

export function resolvePortalAssetUrl(assets = [], identity = {}, fallback = '') {
  const asset = currentPortalAsset(assets, identity);
  return asset?.public_url || asset?.publicUrl || fallback;
}

export async function publishPortalAssetFile(file, {
  scope = 'portal',
  entityRef = 'global',
  assetKey = 'asset',
  metadata = {},
  isDemo = false,
} = {}) {
  if (!isConfigured) throw new Error('Supabase is not configured');
  if (!(file instanceof File)) throw new Error('A File object is required');

  const form = new FormData();
  form.append('file', file);
  form.append('scope', scope);
  form.append('entity_ref', entityRef);
  form.append('asset_key', assetKey);
  form.append('metadata', JSON.stringify(metadata || {}));
  form.append('is_demo', String(Boolean(isDemo)));

  const { data, error } = await supabase.functions.invoke('publish-portal-asset', {
    body: form,
  });
  if (error) throw error;

  const asset = data?.asset || data;
  return normalizePortalAsset(asset);
}
